import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getArchives } from '../../../api/archives.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import { IArchiveItem, IGetArchives } from '../../../models/archives.model';
import { RootState } from '../../../store/rootReducer';

const ArchiveManage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0011 = 운영자료실 카테고리
  const BR0011 = commonCodes.find(
    (item) => item.commonCode === 'BR0011',
  )?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetArchives>();

  const [archiveCategoryCode, setArchiveCategoryCode] = useState<number>();
  const [field, setField] = useState('title');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.archiveCategoryCode) {
      _param.archiveCategoryCode = Number(query.archiveCategoryCode);
      setArchiveCategoryCode(_param.archiveCategoryCode);
    }
    if (query.title) {
      setField('title');
      _param.title = String(query.title);
      setKeyword(_param.title);
    }
    if (query.content) {
      setField('content');
      _param.content = String(query.content);
      setKeyword(_param.content);
    }
    if (query.regMemName) {
      setField('regMemName');
      _param.regMemName = String(query.regMemName);
      setKeyword(_param.regMemName);
    }

    execute(getArchives('OP', _param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (archiveCategoryCode) {
      param.archiveCategoryCode = archiveCategoryCode;
    }

    switch (field) {
      case 'title':
        param.title = keyword;
        break;
      case 'content':
        param.content = keyword;
        break;
      case 'regMemName':
        param.regMemName = keyword;
        break;
    }

    history.push(`/archive/manage?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IArchiveItem> = [
    {
      title: t('archive.exam.column1'),
      dataIndex: 'archiveCode',
      key: 'archiveCode',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.archiveCode - b.archiveCode,
    },
    {
      title: t('archive.exam.column2'),
      dataIndex: 'archiveCategoryCode',
      key: 'archiveCategoryCode',
      align: 'center',
      width: '10%',
      sorter: (a, b) =>
        a.archiveCategoryCode?.localeCompare(b.archiveCategoryCode),
      render: (value) => getCommonCodeLabel('BR0011', value, i18n.language),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.title?.localeCompare(b.title),
      render: value => <div className="text-left ant-table-cell-ellipsis">{value}</div>
    },
    {
      title: t('archive.learn.column1'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    },
    {
      title: t('archive.learn.column2'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('archive.learn.column3'),
      dataIndex: 'readCount',
      key: 'readCount',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.readCount - b.readCount,
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-archive-big.svg"
      parentMenu={t('common.menu.resources')}
      currentMenu={t('archive.manage.title1')}
      onlyTitle={true}
    />	
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('archive.exam.column2')}</label>
            <div>
              <Select
                placeholder={t('archive.exam.column2')}
                value={archiveCategoryCode}
                onChange={(value) => setArchiveCategoryCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {BR0011?.map((item: any) => {
                  return (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {i18n.language === 'ko' ? item.korName : item.engName}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
          <div>
            <Select
              placeholder={t('archive.manage.placeholder1')}
              value={field}
              onChange={(value) => setField(value)}
            >
              <Select.Option value="title">{t('common.column.title')}</Select.Option>
              <Select.Option value="content">{t('common.column.contents')}</Select.Option>
              <Select.Option value="regMemName">{t('common.column.writer')}</Select.Option>
            </Select>
          </div>
          <input
            type="text"
            className="form-control w-240p"
            placeholder={t('archive.learn.placeholder1')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
          />
          <div className="comm-btn__area">
            <Button
              className="btn-basic small black"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />      

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            {auth?.hqYn === 'Y' ? (
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/archive/manage/create')}
                text={t('archive.learn.button1')}
              />
            ) : null}
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/archive/manage/${
                      record.archiveCode
                    }?${QueryString.stringify(param)}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="archiveCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/archive/manage"
            />
          </div>
          <div className="comm-btn__area right">
            {auth?.hqYn === 'Y' ? (
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/archive/manage/create')}
                text={t('archive.learn.button1')}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArchiveManage;
